import { SaveOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';
import RichTextEditor from '../../../../../core/components/Quill/RichTextEditor';
import { Company } from '../models/company-profile.model';
import CompanyLogoControl from './CompanyLogoControl';

interface Props {
  company: Company;
  dirty: boolean;
  logo: string;
  saving: boolean;
  onCancel: () => void;
  onChange: (name: 'name' | 'website' | 'aboutUs', value: string) => void;
  onLogoRemove: () => void;
  onLogoUpload: UploadProps['onChange'];
  onSave: () => void;
  validateLogo: (file: RcFile) => boolean;
}

const CompanyEditor = ({
  company,
  dirty,
  logo,
  saving,
  onCancel,
  onChange,
  onLogoRemove,
  onLogoUpload,
  onSave,
  validateLogo,
}: Props) => {
  const nameInvalid = !company.name.trim();
  const websiteInvalid = !company.website.trim();
  const formInvalid = nameInvalid || websiteInvalid;

  return (
    <section className="company-editor" aria-labelledby="company-editor-title">
      <nav className="company-editor__nav" aria-label="Company profile fields">
        <strong>Company profile</strong>
        <a href="#company-branding">Logo &amp; branding</a>
        <a href="#company-details">Company details</a>
        <a href="#company-about">About company</a>
      </nav>

      <div className="company-editor__content">
        <header className="company-editor__heading">
          <div>
            <h2 id="company-editor-title">General profile</h2>
            <p>Information shown across jobs and candidate communications.</p>
          </div>
          <span
            className={`company-save-state${
              dirty ? ' company-save-state--dirty' : ''
            }`}
          >
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </span>
        </header>

        <div id="company-branding" className="company-editor__section">
          <CompanyLogoControl
            companyName={company.name}
            disabled={saving}
            logo={logo}
            onRemove={onLogoRemove}
            onUploadChange={onLogoUpload}
            validateFile={validateLogo}
          />
        </div>

        <div id="company-details" className="company-editor__section">
          <div className="company-editor__fields">
            <div className="company-field">
              <label htmlFor="companyName">Company name</label>
              <Input
                id="companyName"
                name="name"
                value={company.name}
                status={nameInvalid ? 'error' : ''}
                onChange={(event) => onChange('name', event.target.value)}
                aria-describedby={nameInvalid ? 'companyNameError' : undefined}
              />
              {nameInvalid ? (
                <span id="companyNameError" className="company-field__error">
                  Enter the company name.
                </span>
              ) : null}
            </div>
            <div className="company-field">
              <label htmlFor="companyWebsite">Website</label>
              <Input
                id="companyWebsite"
                name="website"
                value={company.website}
                status={websiteInvalid ? 'error' : ''}
                onChange={(event) => onChange('website', event.target.value)}
                aria-describedby={
                  websiteInvalid ? 'companyWebsiteError' : undefined
                }
              />
              {websiteInvalid ? (
                <span id="companyWebsiteError" className="company-field__error">
                  Enter the company website.
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div id="company-about" className="company-editor__section">
          <div className="company-field">
            <label htmlFor="companyAbout">About the company</label>
            <p className="company-field__hint">
              Give recruiters and candidates a concise description of the
              organization.
            </p>
            <RichTextEditor
              id="companyAbout"
              className="company-about-editor"
              placeholder="Describe the company, its mission, and the type of work it offers…"
              value={company.aboutUs}
              onChange={(value) => onChange('aboutUs', value)}
            />
          </div>
        </div>

        <footer className="company-editor__actions">
          <button
            className="company-secondary-action"
            type="button"
            disabled={saving}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="company-primary-action"
            type="button"
            disabled={!dirty || formInvalid || saving}
            onClick={onSave}
          >
            <SaveOutlined aria-hidden />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </section>
  );
};

export default CompanyEditor;
